import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
} from "@/components/ui/sheet"
import { Icon } from "@/icons/icon"
import { cn, EnumsOf } from "@/lib/utils"
import { ComponentPropsWithRef, useEffect, useState } from "react"

export interface PickerOption {
  label: string
  value: string
}

export interface PickerWithSheetProps {
  className?: string
  options?: PickerOption[]
  value?: string
  deptId: string
  indicators: Record<string, PickerButtonIndicator>
  onChange?: (value: string, index: number) => void
}

export default function PickerWithSheet({
  className,
  options,
  value,
  deptId,
  indicators,
  onChange,
}: PickerWithSheetProps) {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [showSheet, setShowSheet] = useState(false)

  useSyncWithValue()

  function handleSelect(index: number) {
    setSelectedIndex(index)

    if (onChange) {
      onChange(options?.at(index)?.value || "", index)
    }
  }

  function openSheet() {
    setShowSheet(true)
  }

  function useSyncWithValue() {
    useEffect(() => {
      const index = options?.findIndex((item) => item.value === value)
      setSelectedIndex(typeof index === "number" ? index : -1)
    }, [value, options])
  }

  return (
    <div className={cn("relative", className)}>
      <div className="flex gap-2 max-w-screen overflow-auto hide-scrollbar pr-12">
        {options?.map((item, index) => (
          <PickerButton
            key={item.value}
            selected={index === selectedIndex}
            indicator={indicators[`${deptId}.${item.value}`]}
            onClick={() => handleSelect(index)}
          >
            {item.label}
          </PickerButton>
        ))}
      </div>

      <button
        className="
          absolute right-0 top-1/2 -translate-y-1/2 size-7 rounded-full 
          bg-blue hover:cursor-pointer flex items-center justify-center
          shadow-drop-shadow active:shadow-none transition-shadow
        "
        type="button"
        onClick={openSheet}
      >
        <Icon name="ChevronRight" className="text-white h-3 w-auto" />
      </button>

      <Sheet open={showSheet} onOpenChange={setShowSheet}>
        <SheetContent side="bottom">
          <SheetHeader className="pt-4 px-4 pb-6 text-xl font-semibold">
            Select Machine
          </SheetHeader>

          <SheetDescription className="sr-only">
            Select the machine to work on
          </SheetDescription>

          <div className="flex flex-wrap justify-center gap-x-1.5 gap-y-6 px-4 pb-6">
            {options?.map((option, index) => (
              <PickerButton
                selected={index === selectedIndex}
                indicator={indicators[`${deptId}.${option.value}`]}
                onClick={() => handleSelect(index)}
              >
                {option.label}
              </PickerButton>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}

export const PickerButtonIndicator = {
  Success: "Success",
  Warning: "Warning",
} as const
export type PickerButtonIndicator = EnumsOf<typeof PickerButtonIndicator>

interface PickerButtonProps extends ComponentPropsWithRef<"button"> {
  selected?: boolean
  indicator?: PickerButtonIndicator
}

export function PickerButton({
  selected,
  children,
  type = "button",
  indicator,
  ...props
}: PickerButtonProps) {
  return (
    <button
      className={cn(
        `
          rounded-full h-7 border border-input-border hover:cursor-pointer
          px-4.5 bg-white font-medium text-label-text text-sm flex gap-2
          items-center justify-center min-w-max
        `,
        selected && "border-blue text-blue"
      )}
      type={type}
      {...props}
    >
      {!!indicator && (
        <div
          className={cn(
            "size-1.5 rounded-full",
            indicator === "Success" && "bg-success",
            indicator === "Warning" && "bg-resample"
          )}
        ></div>
      )}

      {children}
    </button>
  )
}
